import React from 'react';
import {useSession} from '../../../../../firebase/UserProvider';
import {useReportStore} from '../../../useReportStore';
import SourcesFolderDialogs from './SourcesFolderDialogs';
import {filterSourcesByQuery, SOURCES_PRESET_FOLDERS} from './sourcesAdapter';
import {buildSourcesViewKey} from './utils/sourceFileSelection';
import useSourcesDataSync from './useSourcesDataSync';
import useSourcesFolderActions from './useSourcesFolderActions';
import useSourcesUploadQueue from './useSourcesUploadQueue';
import useSourcesUploadQueueStore from './useSourcesUploadQueueStore';
import useSourcesSelection from './useSourcesSelection';
import useSourcesReload from './useSourcesReload';
import useSourcesMoveEntry from './useSourcesMoveEntry';
import useSourcesDragAndDrop from './useSourcesDragAndDrop';
import useSourcesFileActions from './useSourcesFileActions';
import useSourcesBulkActions from './useSourcesBulkActions';
import useUnifiedSourcesSearch from './useUnifiedSourcesSearch';
import useSearchScopeCounts from './useSearchScopeCounts';
import useOpenSearchHit from './useOpenSearchHit';
import useSourcesFileViewerStore from '../../../../../stores/useSourcesFileViewerStore';
import useUploadConfig from './useUploadConfig';
import {
    useSourcesExplorerActions,
    useSourcesExplorerDataState,
    useSourcesReportPanelState,
    useSourcesViewerState,
} from './useSourcesSectionStoreSelectors';

const useSourcesSectionController = ({isActive, mode = 'search'}) => {
    const session = useSession();
    const userId = session?.user?.uid || null;
    const isSearchSurface = mode === 'search';

    const {
        reportId,
        showFilters,
        setShowFilters,
        unifiedSourceScope,
        setUnifiedSourceScope,
        unifiedSearchMode,
        setUnifiedSearchMode,
        searchPassageContext,
        setSearchPassageContext,
    } = useSourcesReportPanelState();
    const {
        inputValue,
        debouncedQuery,
        activeView,
        sources,
        connectionState,
        isLoading,
        error,
        warning,
        isStale,
        searchResults,
        searchMeta,
        isSearching,
        searchError,
        scopeCounts,
        isScopeCounting,
    } = useSourcesExplorerDataState();
    const {
        setInputValue,
        setDebouncedQuery,
        setActiveView,
        setSources,
        setConnectionState,
        setIsLoading,
        setError,
        setWarning,
        setIsStale,
    } = useSourcesExplorerActions();
    const {selectedFileId, selectedFileSourceViewKey} = useSourcesViewerState();

    const [createdFolderFlash, setCreatedFolderFlash] = React.useState({id: null, token: 0});
    const createdFolderFlashClearTimerRef = React.useRef(null);

    const [shareOpen, setShareOpen] = React.useState(false);
    const [shareTarget, setShareTarget] = React.useState(null);

    const handleFileShareRequest = React.useCallback((file) => {
        if (!file) {
            return;
        }
        // Shares are keyed on the canonical data item. For a non-share REFERENCE row the
        // canonical id lives in `referencedDataItemId`; for an owned FILE row it's the id itself.
        const canonicalId = file.referencedDataItemId || file.id;
        setShareTarget({...file, id: canonicalId});
        setShareOpen(true);
    }, []);

    const handleCloseShareDialog = React.useCallback(() => {
        setShareOpen(false);
        setShareTarget(null);
    }, []);

    const {loadSources} = useSourcesDataSync({
        isActive,
        activeView,
        userId,
        setSources,
        setConnectionState,
        setIsLoading,
        setError,
        setWarning,
        setIsStale,
    });

    const {reloadActiveViewSilently, scheduleReload} = useSourcesReload({
        activeView,
        loadSources,
    });

    // Classification is edited from the open document header. Refresh the file list when a
    // save broadcasts, so a later reopen does not show the previous markings.
    React.useEffect(() => useSourcesFileViewerStore.subscribe(
        (state) => state.classificationUpdate,
        (update) => {
            if (update?.fileId) {
                reloadActiveViewSilently();
            }
        },
    ), [reloadActiveViewSilently]);

    const handleFolderCreated = React.useCallback((folder) => {
        const folderId = folder?.id;
        if (!folderId) {
            return;
        }

        if (createdFolderFlashClearTimerRef.current) {
            window.clearTimeout(createdFolderFlashClearTimerRef.current);
        }

        setCreatedFolderFlash({
            id: String(folderId),
            token: Date.now(),
        });

        createdFolderFlashClearTimerRef.current = window.setTimeout(() => {
            setCreatedFolderFlash({id: null, token: 0});
            createdFolderFlashClearTimerRef.current = null;
        }, 1500);
    }, []);

    React.useEffect(() => () => {
        if (createdFolderFlashClearTimerRef.current) {
            window.clearTimeout(createdFolderFlashClearTimerRef.current);
        }
    }, []);

    const {
        openCreateFolderDialog,
        handleFolderClick,
        handleBack,
        openFolderMenu,
        dialogs,
    } = useSourcesFolderActions({
        activeView,
        setActiveView,
        sources,
        setSources,
        userId,
        loadSources,
        onFolderCreated: handleFolderCreated,
    });

    const handleDuplicateUpload = React.useCallback(({message}) => {
        setWarning(message || 'This file is already in My files.');
        setError(null);
    }, [setError, setWarning]);

    const {cancelItem: cancelQueueItem, clearQueue} = useSourcesUploadQueue({
        userId,
        enabled: Boolean(isActive),
        onUploaded: scheduleReload,
        onDuplicate: handleDuplicateUpload,
    });

    const filesSectionLabel = activeView.type === 'root' ? 'Recently Uploaded' : 'Files';
    const baseHeaderTitle = activeView.type === 'root' ? 'Sources' : activeView.name;
    const currentFolderId = activeView.type === 'folder'
        ? (activeView.pathIds || [])[activeView.pathIds?.length - 1] || activeView.id || null
        : null;
    const currentViewKey = React.useMemo(() => buildSourcesViewKey(activeView, currentFolderId), [activeView, currentFolderId]);
    const isSharedPresetRoot = activeView.type === 'preset' && activeView.id === 'shared';

    const enqueueUploads = useSourcesUploadQueueStore((state) => state.enqueueUploads);
    const enqueueWebImports = useSourcesUploadQueueStore((state) => state.enqueueWebImports);
    const {urlImportEnabled} = useUploadConfig();

    // One dedicated dialog per "Add source" entry point (Files / URL / Paste / API) — opened
    // either via the toolbar menu (no pre-populated files) or via drag&drop (always opens the
    // Files dialog, pre-populated with the dropped files, skipping the menu). `openKind` tracks
    // which dialog is currently open; `targetFolderId` is captured at open-time so submission
    // lands in the right folder regardless of which trigger opened the dialog.
    const [addSourceDialogState, setAddSourceDialogState] = React.useState({
        openKind: null,
        initialFiles: null,
        targetFolderId: null,
    });

    const openAddSourceDialog = React.useCallback((kind) => {
        setAddSourceDialogState({openKind: kind, initialFiles: null, targetFolderId: currentFolderId || null});
    }, [currentFolderId]);

    const openAddSourceDialogWithFiles = React.useCallback((files, targetFolderId) => {
        setAddSourceDialogState({
            openKind: 'file',
            initialFiles: files,
            targetFolderId: targetFolderId ?? currentFolderId ?? null,
        });
    }, [currentFolderId]);

    const closeAddSourceDialog = React.useCallback(() => {
        setAddSourceDialogState({openKind: null, initialFiles: null, targetFolderId: null});
    }, []);

    // Files submitted from the Files tab (native picker or drag&drop) go through the upload
    // queue's file pipeline.
    //
    // New signature: fileEntries is an array of { file: File, classification: { level, distribution, compartment } }
    // Each file carries its own classification so mixed-sensitivity batches are fully supported.
    // Legacy callers that pass (File[], sharedClassification) are supported via the fallback branch.
    const handleSubmitFiles = React.useCallback((fileEntries, legacyClassification) => {
        if (!fileEntries?.length || !userId) {
            return;
        }

        // Detect new per-file format: array of { file, classification }
        const isPerFile = fileEntries[0] && typeof fileEntries[0] === 'object' && !(fileEntries[0] instanceof File) && fileEntries[0].file instanceof File;

        if (isPerFile) {
            // Enqueue each file with its own classification — one enqueueUploads call per file
            // so each queue item carries its correct markings.
            fileEntries.forEach(({file, classification = {}}) => {
                enqueueUploads([{file, folderPath: []}], {
                    targetFolderId: addSourceDialogState.targetFolderId || null,
                    classificationLevel: classification.level || null,
                    distribution: Array.isArray(classification.distribution) ? classification.distribution : [],
                    compartment: Array.isArray(classification.compartment) ? classification.compartment : [],
                });
            });
        } else {
            // Legacy: (File[], sharedClassification) — drag&drop or other callers
            const classification = legacyClassification || {};
            const entries = fileEntries.map((file) => ({file, folderPath: []}));
            enqueueUploads(entries, {
                targetFolderId: addSourceDialogState.targetFolderId || null,
                classificationLevel: classification.classificationLevel || null,
                distribution: classification.distribution || [],
                compartment: classification.compartment || [],
            });
        }
    }, [addSourceDialogState.targetFolderId, enqueueUploads, userId]);

    // URL / paste imports land in the folder captured when the dialog was opened (root → null),
    // mirroring file uploads.
    const handleWebImports = React.useCallback((entries) => {
        if (!Array.isArray(entries) || !entries.length || !userId) {
            return;
        }
        enqueueWebImports(entries, {targetFolderId: addSourceDialogState.targetFolderId ?? currentFolderId ?? null});
    }, [addSourceDialogState.targetFolderId, currentFolderId, enqueueWebImports, userId]);

    React.useEffect(() => {
        const timerId = window.setTimeout(() => {
            setDebouncedQuery(inputValue.trim());
        }, 350);

        return () => {
            window.clearTimeout(timerId);
        };
    }, [inputValue, setDebouncedQuery]);

    // M6 federated search: when there's a query, run unified search across Corpus + My Data.
    // Only the search surface runs/shows search; the file-manager surface always shows files.
    // A tag filter alone (empty query) also enters search mode — filter-browse returns every
    // item labeled with the selected tags.
    const searchTagFilter = useReportStore((state) => state.tags);
    const hasTagFilter = Array.isArray(searchTagFilter) && searchTagFilter.length > 0;
    const isSearchMode = isSearchSurface && (Boolean((debouncedQuery || '').trim()) || hasTagFilter);

    // In search mode the panel lists ranked hits, not a folder/source tree — label it accordingly.
    const headerTitle = isSearchMode ? 'Search Results' : baseHeaderTitle;
    useUnifiedSourcesSearch({userId, enabled: isActive && isSearchSurface});
    useSearchScopeCounts({userId, enabled: isActive && isSearchSurface});
    const handleOpenSearchHit = useOpenSearchHit({reportId});

    const displayedSources = React.useMemo(() => {
        const normalizedSources = activeView.type === 'root'
            ? {
                ...sources,
                folders: SOURCES_PRESET_FOLDERS.map((presetFolder) => {
                    const matchedFolder = sources.folders.find((folder) => folder.id === presetFolder.id);
                    return matchedFolder ? {...presetFolder, ...matchedFolder} : presetFolder;
                }),
            }
            : sources;

        // The file-manager surface never filters by the search query.
        return filterSourcesByQuery(normalizedSources, isSearchSurface ? debouncedQuery : '');
    }, [activeView.type, debouncedQuery, sources, isSearchSurface]);

    const {
        selectedEntries,
        selectedCount,
        clearSelection,
        isFolderSelectable,
        isFolderSelected,
        isFileSelected,
        handleFolderSelectionToggle,
        handleFileSelectionToggle,
    } = useSourcesSelection({
        activeViewType: activeView.type,
        currentFolderId,
        displayedSources,
    });

    const moveEntryToFolder = useSourcesMoveEntry({userId});

    const {
        handleDropToCurrentView,
        handleDropToFolder,
        canDropToFolder,
        canDragFolder,
        handleFileDragStart,
        handleFolderDragStart,
    } = useSourcesDragAndDrop({
        activeView,
        currentFolderId,
        isSharedPresetRoot,
        userId,
        selectedEntries,
        moveEntryToFolder,
        reloadActiveViewSilently,
        onFilesDropped: (files, targetFolderId) => openAddSourceDialogWithFiles(files, targetFolderId),
    });

    const {
        filePendingDelete,
        fileDeletionConflict,
        fileDeleteError,
        isDeletingFile,
        handleFileClick,
        handleFileDeleteRequest,
        handleCloseFileDeleteDialog,
        handleFileDelete,
    } = useSourcesFileActions({
        reportId,
        activeView,
        currentFolderId,
        currentViewKey,
        sources,
        userId,
        reloadActiveViewSilently,
    });

    const {
        moveDialogOpen,
        moveTargetFolderId,
        isBulkMoving,
        bulkMoveError,
        moveTargets,
        singleMoveEntry,
        newFolderDialogOpen,
        newFolderName,
        isCreatingSelectionFolder,
        newFolderError,
        deleteSelectionDialogOpen,
        isDeletingSelection,
        deleteSelectionError,
        handleMoveSelection,
        handleCreateFolderWithSelection,
        handleDeleteSelection,
        handleBulkMoveRequest,
        handleSingleFileMoveRequest,
        handleBulkNewFolderRequest,
        handleBulkDeleteRequest,
        handleMoveDialogClose,
        handleMoveTargetChange,
        handleNewFolderDialogClose,
        handleNewFolderNameChange,
        handleDeleteSelectionDialogClose,
    } = useSourcesBulkActions({
        activeView,
        currentFolderId,
        displayedSources,
        sources,
        isFolderSelectable,
        selectedEntries,
        selectedCount,
        clearSelection,
        userId,
        moveEntryToFolder,
        reloadActiveViewSilently,
        setSources,
    });

    const handleSearchInputChange = React.useCallback((event) => {
        setInputValue(event.target.value);
    }, [setInputValue]);

    const handleToggleFilters = React.useCallback(() => {
        setShowFilters(!showFilters);
    }, [setShowFilters, showFilters]);

    const handleSourceScopeChange = React.useCallback((next) => {
        setUnifiedSourceScope(next);
    }, [setUnifiedSourceScope]);

    const handleSearchModeChange = React.useCallback((next) => {
        setUnifiedSearchMode(next);
    }, [setUnifiedSearchMode]);

    const handlePassageContextChange = React.useCallback((event) => {
        setSearchPassageContext(event.target.value);
    }, [setSearchPassageContext]);

    const searchHeaderProps = React.useMemo(() => ({
        inputValue,
        onInputChange: handleSearchInputChange,
        showFilters,
        onToggleFilters: handleToggleFilters,
        sourceScope: unifiedSourceScope,
        onSourceScopeChange: handleSourceScopeChange,
        searchMode: unifiedSearchMode,
        onSearchModeChange: handleSearchModeChange,
        passageContext: searchPassageContext,
        onPassageContextChange: handlePassageContextChange,
        scopeCounts,
        isScopeCounting,
    }), [
        handleSearchInputChange,
        handleToggleFilters,
        inputValue,
        showFilters,
        unifiedSourceScope,
        handleSourceScopeChange,
        unifiedSearchMode,
        handleSearchModeChange,
        searchPassageContext,
        handlePassageContextChange,
        scopeCounts,
        isScopeCounting,
    ]);

    const contentProps = React.useMemo(() => ({
        activeView,
        headerTitle,
        filesSectionLabel,
        displayedSources,
        connectionState,
        isLoading,
        error,
        warning,
        isStale,
        userId,
        currentViewKey,
        selectedFileId,
        selectedFileSourceViewKey,
        isSearchMode,
        searchResults,
        searchMeta,
        searchQuery: debouncedQuery,
        isSearching,
        searchError,
        onOpenSearchHit: handleOpenSearchHit,
        onBack: handleBack,
        onOpenCreateFolder: openCreateFolderDialog,
        onFolderClick: handleFolderClick,
        onOpenFolderMenu: openFolderMenu,
        onFileClick: handleFileClick,
        onFileDelete: handleFileDeleteRequest,
        onFileMoveRequest: handleSingleFileMoveRequest,
        onFileShare: handleFileShareRequest,
        onDropToCurrentView: handleDropToCurrentView,
        onDropToFolder: handleDropToFolder,
        onFileDragStart: handleFileDragStart,
        onFolderDragStart: handleFolderDragStart,
        dropEnabled: Boolean(userId),
        dropToCurrentViewEnabled: Boolean(userId) && !isSharedPresetRoot,
        canDropToFolder,
        canDragFolder,
        selectedCount,
        isFolderSelectable,
        isFolderSelected,
        onFolderSelectionToggle: handleFolderSelectionToggle,
        isFileSelected,
        onFileSelectionToggle: handleFileSelectionToggle,
        flashFolderId: createdFolderFlash.id,
        flashFolderToken: createdFolderFlash.token,
        onBulkMoveRequest: handleBulkMoveRequest,
        onBulkNewFolderRequest: handleBulkNewFolderRequest,
        onBulkDeleteRequest: handleBulkDeleteRequest,
        onBulkClearSelection: clearSelection,
        onOpenAddSource: openAddSourceDialog,
        urlImportEnabled,
    }), [
        activeView,
        canDragFolder,
        canDropToFolder,
        clearSelection,
        connectionState,
        currentViewKey,
        displayedSources,
        error,
        filesSectionLabel,
        handleBack,
        handleBulkDeleteRequest,
        handleBulkMoveRequest,
        handleBulkNewFolderRequest,
        handleFileClick,
        handleFileDeleteRequest,
        handleSingleFileMoveRequest,
        handleFileShareRequest,
        handleFileDragStart,
        handleFolderClick,
        handleFolderDragStart,
        handleFolderSelectionToggle,
        handleDropToCurrentView,
        handleDropToFolder,
        headerTitle,
        isFileSelected,
        isFolderSelectable,
        isFolderSelected,
        isLoading,
        isStale,
        openCreateFolderDialog,
        openFolderMenu,
        createdFolderFlash.id,
        createdFolderFlash.token,
        selectedCount,
        selectedFileId,
        selectedFileSourceViewKey,
        userId,
        warning,
        handleFileSelectionToggle,
        openAddSourceDialog,
        urlImportEnabled,
        isSharedPresetRoot,
        isSearchMode,
        searchResults,
        searchMeta,
        isSearching,
        searchError,
        handleOpenSearchHit,
    ]);

    const dialogProps = React.useMemo(() => ({
        folderDialogs: (
            <SourcesFolderDialogs
                menu={dialogs.menu}
                createDialog={dialogs.createDialog}
                renameDialog={dialogs.renameDialog}
                removeDialog={dialogs.removeDialog}
            />
        ),
        fileDeleteDialog: {
            filePendingDelete,
            deletionConflict: fileDeletionConflict,
            error: fileDeleteError,
            isSubmitting: isDeletingFile,
            onClose: handleCloseFileDeleteDialog,
            onConfirm: () => handleFileDelete(undefined),
            onConfirmPlacementOnly: () => handleFileDelete(false),
            onConfirmDeleteEverywhere: () => handleFileDelete(true),
        },
        moveDialog: {
            isOpen: moveDialogOpen,
            onClose: handleMoveDialogClose,
            selectedCount,
            singleItemName: singleMoveEntry ? singleMoveEntry.name : null,
            targetFolderId: moveTargetFolderId,
            onTargetChange: handleMoveTargetChange,
            targets: moveTargets,
            error: bulkMoveError,
            isSubmitting: isBulkMoving,
            isConfirmDisabled: isBulkMoving || (selectedCount === 0 && !singleMoveEntry),
            onConfirm: handleMoveSelection,
        },
        newFolderDialog: {
            isOpen: newFolderDialogOpen,
            onClose: handleNewFolderDialogClose,
            selectedCount,
            name: newFolderName,
            onNameChange: handleNewFolderNameChange,
            error: newFolderError,
            isSubmitting: isCreatingSelectionFolder,
            isConfirmDisabled: isCreatingSelectionFolder || !newFolderName.trim() || selectedCount === 0,
            onConfirm: handleCreateFolderWithSelection,
        },
        deleteSelectionDialog: {
            isOpen: deleteSelectionDialogOpen,
            onClose: handleDeleteSelectionDialogClose,
            selectedCount,
            error: deleteSelectionError,
            isSubmitting: isDeletingSelection,
            isConfirmDisabled: isDeletingSelection || selectedCount === 0,
            onConfirm: handleDeleteSelection,
        },
        addSourceDialog: {
            openKind: addSourceDialogState.openKind,
            onClose: closeAddSourceDialog,
            onSubmitWebImports: handleWebImports,
            onSubmitFiles: handleSubmitFiles,
            initialFiles: addSourceDialogState.initialFiles,
            urlImportEnabled,
        },
        shareDialog: {
            open: shareOpen,
            dataItem: shareTarget,
            onClose: handleCloseShareDialog,
        },
    }), [
        bulkMoveError,
        currentFolderId,
        deleteSelectionDialogOpen,
        deleteSelectionError,
        dialogs.createDialog,
        dialogs.menu,
        dialogs.removeDialog,
        dialogs.renameDialog,
        fileDeleteError,
        fileDeletionConflict,
        filePendingDelete,
        handleCloseFileDeleteDialog,
        handleCreateFolderWithSelection,
        handleDeleteSelection,
        handleDeleteSelectionDialogClose,
        handleFileDelete,
        handleMoveDialogClose,
        handleMoveSelection,
        handleMoveTargetChange,
        handleNewFolderDialogClose,
        handleNewFolderNameChange,
        isBulkMoving,
        isCreatingSelectionFolder,
        isDeletingFile,
        isDeletingSelection,
        moveDialogOpen,
        moveTargetFolderId,
        moveTargets,
        singleMoveEntry,
        newFolderDialogOpen,
        newFolderError,
        newFolderName,
        selectedCount,
        userId,
        addSourceDialogState,
        closeAddSourceDialog,
        handleWebImports,
        handleSubmitFiles,
        urlImportEnabled,
        shareOpen,
        shareTarget,
        handleCloseShareDialog,
    ]);

    const uploadQueueProps = React.useMemo(() => ({
        onCancelItem: cancelQueueItem,
        onClearQueue: clearQueue,
        onOpenExistingFile: handleFileClick,
    }), [cancelQueueItem, clearQueue, handleFileClick]);

    return {
        searchHeaderProps,
        contentProps,
        uploadQueueProps,
        dialogProps,
    };
};

export default useSourcesSectionController;
